/**
 * Utility functions for handling images in bill templates
 */

import { getImageProxyDomain } from './domainConfig';

/**
 * Creates an image element with proper error handling and fallbacks
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for the image
 * @param {string} className - CSS classes
 * @param {Object} style - Inline styles
 * @param {Function} onError - Error callback
 * @param {Function} onLoad - Load callback
 * @returns {JSX.Element} Image element or fallback div
 */
export const createImageWithFallback = (src, alt, className = '', style = {}, onError, onLoad) => {
  if (!src) {
    return (
      <div 
        className={`bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs text-center ${className}`}
        style={style}
      >
        {alt || 'IMAGE'}
      </div>
    );
  }

  // Enhanced style for better html2canvas compatibility
  const enhancedStyle = {
    ...style,
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain',
    // Ensure images are visible for html2canvas
    visibility: 'visible',
    opacity: 1,
    // Remove any potential blocking styles
    filter: 'none',
    transform: 'none',
    // Ensure proper rendering
    imageRendering: 'auto',
    WebkitImageRendering: 'auto',
    MozImageRendering: 'auto',
    msImageRendering: 'auto'
  };

  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      style={enhancedStyle}
      onError={(e) => {
        if (onError) onError(e);
        // Don't hide the image completely, show a fallback instead
        e.target.style.display = 'none';
        // Create a fallback div
        const fallback = document.createElement('div');
        fallback.className = `bg-gray-100 border border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs text-center ${className}`;
        fallback.style.cssText = Object.entries(enhancedStyle).map(([k, v]) => `${k}: ${v}`).join(';');
        fallback.textContent = alt || 'IMAGE';
        e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
      }}
      onLoad={() => {
        if (onLoad) onLoad();
      }}
      crossOrigin="anonymous"
      loading="eager"
      // Additional attributes for better compatibility
      decoding="async"
      draggable={false}
    />
  );
};

/**
 * Creates a logo element with standard styling and fallback
 * @param {string} logoUrl - Logo URL
 * @param {string} companyName - Company name for alt text
 * @param {string} size - Size class (default: 'h-16 w-16')
 * @returns {JSX.Element} Logo element or fallback
 */
export const createLogo = (logoUrl, companyName = 'Company', size = 'h-16 w-16') => {
  return createImageWithFallback(
    logoUrl,
    `${companyName} Logo`,
    `${size} object-contain border border-gray-200 rounded`,
    {},
    null,
    null
  );
};

/**
 * Creates a seal/signature element with standard styling
 * @param {string} imageUrl - Image URL
 * @param {string} altText - Alt text
 * @param {string} size - Size class (default: 'h-16')
 * @returns {JSX.Element} Image element or fallback
 */
export const createSealOrSign = (imageUrl, altText, size = 'h-16') => {
  // Apply specific size constraints for seal and sign
  const isSeal = altText.toLowerCase().includes('seal');
  const isSign = altText.toLowerCase().includes('sign');
  
  let constrainedSize = size;
  if (isSeal) {
    // Seal: Maximum 0.75x0.75 inch (h-6 w-6 in Tailwind) - half of original size
    constrainedSize = 'h-6 w-6';
  } else if (isSign) {
    // Sign: Maximum 1.5x1.5 inch ratio (h-8 w-12 in Tailwind)
    constrainedSize = 'h-8 w-12';
  }
  
  // Debug logging for signature issues
  if (isSign && !imageUrl) {
    console.log('⚠️ No signature URL provided for:', altText);
  } else if (isSign && imageUrl) {
    console.log('✅ Signature URL found for:', altText, imageUrl.substring(0, 50) + '...');
  }
  
  return createImageWithFallback(
    imageUrl,
    altText,
    `${constrainedSize} object-contain border border-gray-200 rounded`
  );
};

/**
 * Creates a UPI QR code element with fallback
 * @param {string} qrUrl - QR code URL
 * @param {string} size - Size class (default: 'w-20 h-20')
 * @returns {JSX.Element} QR image or fallback placeholder
 */
export const createUPIQR = (qrUrl, size = 'w-20 h-20') => {
  if (!qrUrl) {
    return (
      <div className={`${size} bg-gray-200 flex items-center justify-center text-xs text-gray-500 border border-gray-300 rounded`}>
        QR
      </div>
    );
  }

  return createImageWithFallback(
    qrUrl,
    'UPI QR Code',
    `${size} object-contain border border-gray-200 rounded`
  );
};

/**
 * Validates if a URL is accessible and returns a valid image source
 * @param {string} url - URL to validate
 * @returns {Promise<string>} Valid URL or empty string
 */
export const validateImageUrl = async (url) => {
  if (!url) return '';
  
  try {
    const response = await fetch(url, { method: 'HEAD' });
    if (response.ok && response.headers.get('content-type')?.startsWith('image/')) {
      return url;
    }
  } catch (error) {
    // Silent error handling
  }
  
  return '';
};

/**
 * Preloads images to ensure they're available for PDF generation
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @returns {Promise<Array>} Array of successfully loaded images
 */
export const preloadImages = async (imageUrls) => {
  if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
    return [];
  }

  const preloadPromises = imageUrls.map(async (url) => {
    if (!url) return null;
    
    try {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.loading = 'eager';
        img.decoding = 'async';
        
        img.onload = () => {
          resolve({ url, success: true, element: img });
        };
        
        img.onerror = () => {
          resolve({ url, success: false, element: null });
        };
        
        // Set a timeout to prevent hanging
        setTimeout(() => {
          resolve({ url, success: false, element: null, timeout: true });
        }, 8000); // Increased timeout for mobile devices
        
        img.src = url;
      });
    } catch (error) {
      return { url, success: false, element: null, error };
    }
  });

  return Promise.all(preloadPromises);
};

/**
 * Preloads all images in a bill template before html2canvas generation
 * @param {Object} companyDetails - Company details object
 * @param {Object} bankDetails - Bank details object
 * @returns {Promise<boolean>} True if all images loaded successfully
 */
export const preloadBillImages = async (companyDetails = {}, bankDetails = {}) => {
  const imageUrls = [];
  
  // Collect all image URLs
  if (companyDetails.logoUrl) imageUrls.push(companyDetails.logoUrl);
  if (companyDetails.upiQrUrl) imageUrls.push(companyDetails.upiQrUrl);
  if (companyDetails.sealUrl) imageUrls.push(companyDetails.sealUrl);
  if (companyDetails.signUrl) imageUrls.push(companyDetails.signUrl);
  if (bankDetails.sealUrl) imageUrls.push(bankDetails.sealUrl);
  if (bankDetails.signUrl) imageUrls.push(bankDetails.signUrl);
  
  if (imageUrls.length === 0) return true;
  
  try {
    const results = await preloadImages(imageUrls);
    const successCount = results.filter(r => r && r.success).length;
    console.log(`Preloaded ${successCount}/${imageUrls.length} images for bill generation`);
    return successCount > 0; // Return true if at least one image loaded
  } catch (error) {
    console.warn('Error preloading images:', error);
    return false;
  }
};

/**
 * Converts an image to a data URL for PDF generation
 * @param {string} imageUrl - Image URL to convert
 * @returns {Promise<string>} Data URL or empty string on failure
 */
export const convertImageToDataUrl = async (imageUrl) => {
  if (!imageUrl) return '';
  
  try {
    // Handle different image source types
    if (imageUrl.startsWith('data:')) {
      return imageUrl; // Already a data URL
    }
    
    if (imageUrl.startsWith('blob:')) {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }
    
    // External URL
    const response = await fetch(imageUrl, { 
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (response.ok) {
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }
  } catch (error) {
    // Silent error handling for PDF generation
  }
  
  return '';
};

/** In-memory cache so PDF/clipboard capture does not re-fetch the same bill images every time */
const printImageDataUrlCache = new Map();

/** Clear cache (e.g. if needed after company branding change); optional */
export const clearPrintImageDataUrlCache = () => printImageDataUrlCache.clear();

/**
 * Loads an image as a data URL for reliable browser printing.
 * Chrome often omits cross-origin <img> in print preview; embedding as data: fixes that.
 * Tries direct fetch (CORS), then app image proxy /img?u= when configured.
 */
export const fetchImageAsDataUrlForPrint = async (imageUrl) => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('data:')) return imageUrl;
  if (printImageDataUrlCache.has(imageUrl)) return printImageDataUrlCache.get(imageUrl);

  let data = await convertImageToDataUrl(imageUrl);
  if (data) {
    printImageDataUrlCache.set(imageUrl, data);
    return data;
  }

  try {
    const alreadyProxied = /\/img\?u=/.test(imageUrl);
    const proxied = alreadyProxied
      ? imageUrl
      : `${getImageProxyDomain()}/img?u=${encodeURIComponent(imageUrl)}`;
    const resp = await fetch(proxied, { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) return '';
    const blob = await resp.blob();
    const proxiedData = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || '');
      reader.readAsDataURL(blob);
    });
    if (proxiedData) printImageDataUrlCache.set(imageUrl, proxiedData);
    return proxiedData;
  } catch (_) {
    return '';
  }
};

/**
 * Replaces <img src> with data URLs inside a detached DOM node before window.print().
 */
export const inlineImagesForPrint = async (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      try {
        const src = img.getAttribute('src');
        if (!src || src.startsWith('data:')) return;
        const dataUrl = await fetchImageAsDataUrlForPrint(src);
        if (dataUrl) {
          img.setAttribute('src', dataUrl);
          img.removeAttribute('crossorigin');
        }
      } catch (_) {
        /* keep original src */
      }
    })
  );
};
